import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// Shared read-only review/summary field helpers. Originally in sales-orders/readiness
// (86d3ch831); relocated here (86d3hm2vr) so both the Sales Order and Cutting List posting
// modals can reuse them without a cross-feature import. Zero domain dependencies.

export type ResolvedAddr = { label: string | null; text: string } | null;

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="border-b pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      {children}
    </section>
  );
}

// Default two-column dl; pass `className` to widen (e.g. lg:grid-cols-3) — tailwind-merge
// resolves the grid-cols-* override.
export function Grid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-1 text-sm', className)}>{children}</dl>
  );
}

export function Row({
  label,
  value,
  mono,
  className,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono font-medium' : 'font-medium'}>
        {value || <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

// Read-only checkbox mirroring the form's flag/requirement checkboxes: a filled box +
// emphasized label when on, a muted empty box when off.
export function CheckItem({ label, on }: { label: string; on: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span
        className={cn(
          'flex h-3.5 w-3.5 items-center justify-center rounded-sm border',
          on ? 'border-foreground bg-foreground text-background' : 'border-muted-foreground/40',
        )}
      >
        {on && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
      </span>
      <span className={on ? 'font-medium text-foreground' : 'text-muted-foreground'}>{label}</span>
    </span>
  );
}

export function ChecklistRow({
  label,
  items,
}: {
  label: string;
  items: Array<{ label: string; on: boolean }>;
}) {
  return (
    <div className="col-span-full">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
        {items.map((it) => (
          <CheckItem key={it.label} label={it.label} on={it.on} />
        ))}
      </dd>
    </div>
  );
}

// Address value: the saved-address label (bold) + the full address text. Spans the full
// row since the text can be long.
export function AddressRow({
  label,
  addr,
  fallback = '—',
}: {
  label: string;
  addr: ResolvedAddr;
  fallback?: string;
}) {
  return (
    <div className="col-span-full">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">
        {addr ? (
          <>
            {addr.label && <span className="text-foreground">{addr.label}</span>}
            {addr.label && addr.text && <span className="text-muted-foreground"> · </span>}
            {addr.text && (
              <span className={addr.label ? 'font-normal text-muted-foreground' : undefined}>
                {addr.text}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">{fallback}</span>
        )}
      </dd>
    </div>
  );
}
